import { Setter } from "../../services/ComService";
import { ObjectKind } from "../models/objectKinds";
import { PublishKind } from "../models/publishKinds";
import { IMaterialItem, MaterialItemDTO } from "./MaterialItemDTO";
import { DayDTO, IDay } from "./DayDTO";
import { ISeminar, SeminarDTO } from "./SeminarDTO";
import { ITrainingItem, TrainingItemDTO } from "./TrainingItemDTO";
import ModuleDTO, { IModule } from "./ModuleDTO";
import { ISeminarDay, SeminarDayDTO } from "./SeminarDayDTO";
import DayModuleDTO, { IDayModule } from "./DayModuleDTO";
import { IProp } from "./PropDTO";
import { EventDTO, IEvent } from "./EventDTO";
import { DayDiff, DisplayDate, DisplayDateTimeNumeric, DisplayPHPDate } from "../../services/DateTime";
import { PersonKind } from "../views/Person/PersonKind";
import { NeedleExplorer } from "../../services/NeedleExplorer";
import ItemPersonDTO from "./ItemPersonDTO";
import mainservice from "../../services/MainService";
import AvailablityDTO from "./AvailablityDTO";
import { EventCopyModalState } from "../views/Event/EventCopyModal";

type Item = EventDTO | SeminarDTO | SeminarDayDTO | DayDTO | DayModuleDTO | ModuleDTO | TrainingItemDTO | MaterialItemDTO
type IItem = IEvent | ISeminar | ISeminarDay | IDay | IDayModule | IModule | ITrainingItem | IMaterialItem
export type CompactType = '' | 'yes' | 'name' | 'participated' | 'staffEvents' | 'noCompany'
export default class BuildingBlocksDTO {
  elementKindPage: ObjectKind = ObjectKind.none
  currentPublishKind?: PublishKind = PublishKind.isTemplate
  showItems: Item[] = []
  limit: number = 0 // 0 means, use api default. Any value > 0 will limit the number of items 

  customerId: number = -1
  customerLocationId: number = -1
  locationId: number = -1
  personId: number = -1
  page: number = 0

  tagIdString: string = ''

  minDate: string = ''
  maxDate: string = ''

  onlyWithoutCustomer?: boolean = false
  onlyWithoutCustomerLocation?: boolean = false
  onlyWithoutLocation?: boolean = false
  personKind: PersonKind = PersonKind.generic
  compact: CompactType = ''
  leads: string = ''
  props?: IProp[] = []
  needle?: string = ''
  deleted: string = '0'
  showEmptyElement: boolean = true
  ignoreDate?: '' | 'ignore' = ''
  mode?: string
  sort?: string

  constructor(o?: {
    customerId?: number,
    customerLocationId?: number,
    locationId?: number,
    personId?: number,
    onlyWithoutCustomer?: boolean,
    onlyWithoutCustomerLocation?: boolean,
    onlyWithoutLocation?: boolean,
    personKind?: PersonKind,
    compact?: CompactType,
    leads?: string,
    elementKindPage?: ObjectKind,
    mode?: 'normal' | 'trash' | 'staffEvents',
    showEmptyElement?: boolean,
    minDate?: string,
    maxDate?: string,
    publishKind?: PublishKind,
    ignoreDate?: '' | 'ignore',
    limit?: number,
    needle?: string,
    sort?: string,
  }) {
    if (o?.needle !== undefined) {
      this.needle = o.needle
    }
    if (o?.publishKind !== undefined) {
      this.currentPublishKind = o.publishKind
    }
    if (o?.ignoreDate) {
      this.ignoreDate = o.ignoreDate
    }
    if (o?.showEmptyElement !== undefined) {
      this.showEmptyElement = o.showEmptyElement
    }
    if (o?.customerId !== undefined) {
      this.customerId = o.customerId
    }
    if (o?.customerLocationId !== undefined) {
      this.customerLocationId = o.customerLocationId
    }
    if (o?.locationId !== undefined) {
      this.locationId = o.locationId
    }
    if (o?.personId !== undefined) {
      this.personId = o.personId
    }
    if (o?.personKind) {
      this.personKind = o.personKind
    }
    // Set Flag for server, so that it will only get as deleted marked entries:
    if (o?.mode === 'trash') {
      this.deleted = '1'
    }
    this.mode = o?.mode || 'standard'
    if (o?.minDate) {
      this.minDate = o.minDate
    }
    if (o?.maxDate) {
      this.maxDate = o.maxDate
    }
    this.limit = o?.limit || 0
    this.leads = o?.leads || ''
    this.compact = o?.compact || ''
    this.onlyWithoutCustomer = !!o?.onlyWithoutCustomer
    this.onlyWithoutCustomerLocation = !!o?.onlyWithoutCustomerLocation
    this.onlyWithoutLocation = !!o?.onlyWithoutLocation
    if (o?.elementKindPage) {
      this.elementKindPage = o.elementKindPage
    }
    if (o?.sort) {
      this.sort = o.sort
    }
  }

  public ShowItems() {
    return this.showItems
  }

  public ShowEvents() {
    let events = this.showItems as EventDTO[]
    let out: EventDTO[] = []
    events.forEach(e => {
      let appointments = e.getAppointments()
      let appointmentAmount = appointments.length
      if (appointmentAmount > 1) {
        appointments.forEach((a, index) => {
          const eventItem = {...e, ...{
            startDate: {
              date: a.getV1()
            },
            endDate: {
              date: a.getV2()
            },
            titleSuffix: `(${index + 1}/${appointmentAmount})`,
            appointmentKey: 'tag-' + a.key2,
          }}
          out.push(new EventDTO(eventItem))
        })
      } else {
        out.push(e)
      }
    })
    return out.sort((a,b) => {
      const asv = a.getStartString()
      const bsv = b.getStartString()
      if (asv < bsv) {
        return -1
      }
      return 1
    })
  }

  public async reload() {
    await this.setElementKindPage(0, this.elementKindPage, this.currentPublishKind)
  }

  public concatElements(page: number, item: Item, items: Item[]): Item[] {
    if (page === 0) {
      return [item, ...items]
    }
    return items
  }

  public async setElementKindPage(page: number, key?: ObjectKind, publishKind?: PublishKind, needle?: string, props?: IProp[]) {
    if (isNaN(page)) {
      page = 0
    }
    this.page = page
    if (props === undefined) {
      props = this.props
    } else {
      this.props = props
    }
    if (needle === undefined) {
      needle = this.needle
    } else {
      this.needle = needle
    }
    this.elementKindPage = (key === undefined) ? this.elementKindPage : key
    this.currentPublishKind = publishKind
    const kind = (publishKind === undefined) ? PublishKind.isTemplate : publishKind
    const items = await this.getElementsFromServer(page, this.elementKindPage, kind, needle, props)
    let showItems: Item[] = []
    switch (this.elementKindPage) {
      case ObjectKind.event:
        showItems = items.map((item: IEvent) => new EventDTO(item))
        break
      case ObjectKind.seminar:
        showItems = items.map((day: ISeminar) => new SeminarDTO(day))
        break
      case ObjectKind.day:
        showItems = this.concatElements((this.showEmptyElement ? page : -1), // If page is 0 empty element is beeing added. In Bausteineditor we never want to do that
          new DayDTO({
            id: -1,
            name: 'leerer Tag',
          }),
          items.map((day: IDay) => new DayDTO(day))
        )
        break
      case ObjectKind.module:
        showItems = this.concatElements((this.showEmptyElement ? page : -1),
          new ModuleDTO({
            id: -1,
            name: 'leere Sequenz',
            duration: 60,
            publishKind: PublishKind.isTemplate,
          }),
          items.map((item: IModule) => new ModuleDTO(item))
        )
        break
      case ObjectKind.trainingItem:
        showItems = this.concatElements((this.showEmptyElement ? page : -1),
          new TrainingItemDTO({
            id: -1,
            name: 'leere Kollektion'
          }),
          items.map((item: ITrainingItem) => new TrainingItemDTO(item))
        )
        break
      case ObjectKind.materialItem:
        showItems = this.concatElements((this.showEmptyElement ? page : -1),
          new MaterialItemDTO({
            id: -1,
            name: 'Individuelles Material'
          }),
          items.map((item: IModule) => new MaterialItemDTO(item))
        )
        break
      default:
        console.log('this did not work!!!', this.elementKindPage)
        break
    }
    if (page === 0) {
      this.showItems = showItems
      return
    }
    // If page is greater then 0 -> concat more items!
    this.showItems = [...this.showItems, ...showItems]
    return showItems.length
  }

  public extractEventIdFromNeedle(s: string): { needle: string, flatProps: string[] } {
    let needle: string = s
    let props: string[] = []
    const eventIdScope = /#([0-9]+) ?/
    const eventIdMatch = s.match(eventIdScope)
    if (eventIdMatch !== null) {
      props.push(`data,uniqueEventId,${eventIdMatch[1]},,start`)
      needle = needle.replace(eventIdScope, '')
    }
    return {
      needle: needle,
      flatProps: props,
    }
  }

  private getUrl() {
    if (this.mode === 'staffEvents') {
      return 'spt/getStaffEvents'
    }
    return 'spt/getList'
  }

  public async getElementsFromServer(page: number, objectKind: ObjectKind, publishKind: PublishKind, needle?: string, props?: IProp[]) {
    const url = this.getUrl()
    const needleResult = this.extractEventIdFromNeedle(needle || '')
    let flatPropsArray = needleResult.flatProps
    if (props) {
      flatPropsArray = flatPropsArray.concat(props.map(p => [p.key1, p.key2, p.value1].join(',')))
    }
    const flatProps = flatPropsArray.join(';')
    const minDate = flatProps.search(/(uniqueEventId|offerId)/) > -1 ? 'ignore' :this.ignoreDate || this.minDate
    const query = {
      publishKind: publishKind,
      objectKind: objectKind,
      customerId: this.customerId,
      customerLocationId: this.customerLocationId,
      locationId: this.locationId,
      personId: this.personId,
      props: flatProps,
      minDate: minDate,
      onlyWithoutCustomer: this.onlyWithoutCustomer,
      onlyWithoutCustomerLocation: this.onlyWithoutCustomerLocation,
      onlyWithoutLocation: this.onlyWithoutLocation,
      personKind: PersonKind[this.personKind],
      mode: this.mode,
      tags: this.tagIdString,
      compact: this.compact,
      leads: this.leads,
      page: page,
      deleted: this.deleted,
      limit: this.limit,
      sort: this.sort,
      ...NeedleExplorer(needleResult.needle)
    }
    const data = await Setter(url, query)
    // Definieren, bis wohin dieser Datenschnipsel geht:
    if (data.maxDate) {
      if (page === 0) {
        this.maxDate = data.maxDate
      } else {
        if (data.maxDate > this.maxDate) {
          this.maxDate = data.maxDate
        }
      }
    }
    try {
      return (data as { items: IItem[] }).items
    } catch (error) {
      console.error('problem with datasource ', url, query)
      return []
    }
  }

  public getMaxDate() {
    return new Date(this.maxDate)
  }

  public removeItem(id: number) {
    this.showItems = this.showItems.filter(i => i.id !== id)
  }

  public async cloneItem(id: number, objectKind: ObjectKind, publishKind?: PublishKind, options?: EventCopyModalState) {
    const tI = this.showItems.find(i => i.id === id)
    if (!tI) { return }
    const now = new Date()
    const dateString = DisplayDateTimeNumeric(now)
    await tI.clone(-1, objectKind, tI.name + ' (Kopie ' + dateString + ')', publishKind, undefined, undefined, options)
    await this.reload()
  }

  private getMinMaxDate(items: Item[]) {
    let minRaw = this.minDate
    /*
    let maxRaw = this.minDate
    items.forEach(i => {
      if (i.enddate && i.enddate > maxRaw) {
        maxRaw = i.enddate
      }
      if (i.startdate && i.startdate < minRaw) {
        minRaw = i.startdate
      }
    })
    const max = new Date(maxRaw)
    */
    const min = new Date(minRaw)
    const max = this.getMaxDate()
    const diffDays = DayDiff(min, max)
    return {
      min,
      max,
      diffDays
    }
  }

  public prepareGantt(items: Item[]) {
    const {min, max, diffDays} = this.getMinMaxDate(items)
    // now we get the position of each element:
    return {
      min: min,
      max: max,
      diffDays: diffDays,
      items: items.map(i => {
        let cellsBefore = 0
        let cellsAfter = 0
        let span = diffDays + 0
        if (i.startdate) {
          cellsBefore = DayDiff(min, new Date(i.startdate))
          span -= cellsBefore
        }
        if (i.enddate) {
          cellsAfter = DayDiff(max, new Date(i.enddate))
          span -= cellsAfter
        }
        return {
          id: i.id,
          cellsBefore: cellsBefore,
          cellsAfter: cellsAfter,
          span: span,
        }
      })
    }
  }

  public getTrainersGantt(items: EventDTO[]): TrainerGanttRow[] {
    // const items = this.ShowEvents()
    const {min, max, diffDays} = this.getMinMaxDate(items)
    let trainers: TrainerGanttRow[] = []
    items.forEach((item: EventDTO) => {
      // Jeden Trainer betrachten und wenn nicht vorhanden zu trainer hinzufügen
      // Für jeden Trainer die passenden cells erstmal leer auffüllen.
      // Dann die hoverContent auffüllen und die Klasse des cells passend setzen
      item.trainers?.forEach((trainer: ItemPersonDTO) => {
        // Find trainer in trainers
        let oldTrainer = trainers.find(t => t.trainerId === trainer.id)
        if (!oldTrainer) {
          let cells: TrainerGanttCell[] = []
          for (let i = 0; i < diffDays + 1; i++) {
            let cellDate = new Date(min).setDate(min.getDate() + i)
            let cellDateKey = DisplayPHPDate(new Date(cellDate))
            cells.push({
              key: `gantt-cell-${trainer.id}-${cellDateKey}`,
              personId: trainer.id,
              dateKey: cellDateKey,
              name: 'name',
              color: '',
              bgColor: '',
              displayDate: DisplayDate(new Date(cellDate)),
              displayName: DisplayDate(new Date(cellDate)),
              className: '',
              onClick: undefined,
              hoverContent: [],
            })
          }
          trainers.push({
            trainerId: trainer.id,
            key: 'trainer-' + trainer.id,
            name: trainer.firstname + ' ' + trainer.lastname,
            lastName: trainer.lastname,
            onClick: () => {
              mainservice.navTo([['view', 'spPersonEdit'], ['id', trainer.id]])
            },
            cells: cells,
          })
          oldTrainer = trainers.find(t => t.trainerId === trainer.id)
        }
        // Für jeden Tag, den Item statt findet die Zelle füllen:
        let currentDate = item.startdate ? new Date(item.startdate) : null
        let endDate = item.enddate ? new Date(item.enddate) : null
        while(oldTrainer && currentDate && endDate && currentDate <= endDate) {
          let dateKey = DisplayPHPDate(currentDate)

          let cell = oldTrainer.cells.find(c => {
            return c.dateKey === dateKey
          })
          let cellKey = `gantt-cell-${trainer.id}-${item.id}-${dateKey}`
          if (cell && !cell.hoverContent.find(c => c.key === cellKey)) {
            cell.className = 'active'
            cell.dateKey = dateKey
            cell.hoverContent.push({
              key: cellKey,
              displayName: item.name,
              className: 'active',
              onClick: () => {
                mainservice.navTo([['view', 'spVeranstaltungsDetails'], ['id', item.id]])
              },
            })
            if (cell.hoverContent.length > 1) {
              cell.className = 'overcrowding'
            }
          }
          // Increment currentDate
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1))
        }
      })
    })
    return trainers.sort((a, b) => {
      if (a.lastName < b.lastName) {
        return -1
      }
      return 1
    })
  }

  public async getTrainersGantt90(items: EventDTO[], timeline: AvailablityDTO): Promise <TrainerGantt90Schema> {
    // const timelineItems = 
    // console.log('getTrainersGantt90 items', items)
    await timeline.getFromServer(new Date(this.minDate), this.page)
    const data = this.getTrainersGantt(items);
    let out: TrainerGantt90Schema = {
      headers: [],
      rows: [],
    }
    data.forEach((trainer, trainerIndex) => {
      const noteLeftRightClass = (trainerIndex < data.length / 2) ? 'noteRight ' : 'noteLeft'
      
      out.headers.push({
        trainerId: trainer.trainerId,
        onClick: trainer.onClick,
        key: trainer.key,
        name: trainer.name,
        lastName: trainer.lastName,
      })
      trainer.cells.forEach((cell, cellIndex) => {
        const theDate = new Date(cell.dateKey)
        if (!out.rows[cellIndex]) {
          const KW = (function (date: Date) {
            if (theDate.getDay() !== 1) {
              return '';
            }
            const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
            const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
            return `${Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)}`;
          })(theDate);

          out.rows[cellIndex] = {
            key: 'trainerGantt90Row-' + cell.dateKey,
            name: cell.displayDate,
            calendarWeek: KW,
            weekdayName: theDate.toLocaleDateString('de-DE', {weekday: 'short'}),
            cells: [],
            className: `weekday-class-${theDate.getDay()}`
          }
        }
        out.rows[cellIndex].cells.push(
          {
            ...cell,
            className: `${cell.className} ${noteLeftRightClass} ${timeline.getTimeClassName(trainer.trainerId, theDate)}`,
            name: timeline.getTimeName(trainer.trainerId, theDate),
            color: timeline.getTimeColor(trainer.trainerId, theDate),
            bgColor: timeline.getTimeBgColor(trainer.trainerId, theDate),
          }
        )
      })
    })
    // Datum und Kalenderwoche hinzufügen:
    return out
  }

}

export type TrainerGanttCell = {
  key: string,
  dateKey: string,
  displayName: string,
  className: string,
  personId: number,
  name: string,
  displayDate: string,
  bgColor: string,
  color: string,
  onClick?: () => void,
  hoverContent: {
    key: string,
    displayName: string,
    className: string,
    onClick?: () => void,
  }[]
}

export interface TrainerGanttPerson {
  trainerId: number,
  onClick: () => void,
  key: string,
  name: string,
  lastName: string,
}

export interface TrainerGanttRow extends TrainerGanttPerson {
  cells: TrainerGanttCell[]
}

export type TrainerGantt90Schema = {
  headers: TrainerGanttPerson[],
  rows: {
    key: string,
    name: string,
    calendarWeek: string,
    weekdayName: string,
    className: string,
    cells: TrainerGanttCell[]
  }[]
}